import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
    ArrowLeft, Sparkles, RefreshCw, Loader2, Search, Filter,
    CheckCircle2, AlertCircle,
} from "lucide-react";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
    adminListIllustrations, adminRegenerateIllustration, formatApiError,
} from "@/lib/api";

/**
 * Admin moderation surface for the painterly veggie catalog.
 *
 * Lists every catalog crop with a 3-bucket strip (sprout / growing / mature).
 * Each bucket cell shows the rendered PNG (or a "Missing" placeholder) plus
 * a "Regenerate" button that fires `/api/admin/illustrations/regenerate`
 * via Gemini Nano Banana. Useful for spot-fixing illustrations that
 * drifted (e.g. lettuce → cabbage, basil → red leaves).
 *
 * No bulk-generate UI here on purpose — that's a CLI run.
 */

const BUCKETS = ["sprout", "growing", "mature"];

function StatusPill({ ratio }) {
    if (ratio >= 1) return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[var(--vm-sage)]/15 text-[var(--vm-sage)] text-[10px] uppercase tracking-wide font-semibold">
            <CheckCircle2 className="w-3 h-3" /> Complete
        </span>
    );
    if (ratio === 0) return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[var(--vm-secondary)]/15 text-[var(--vm-secondary)] text-[10px] uppercase tracking-wide font-semibold">
            <AlertCircle className="w-3 h-3" /> Missing
        </span>
    );
    return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[var(--vm-accent)]/15 text-[var(--vm-accent)] text-[10px] uppercase tracking-wide font-semibold">
            Partial
        </span>
    );
}

function BucketCell({ row, bucket, busy, onRegen }) {
    const tile = row.buckets[bucket];
    const exists = !!tile?.exists;
    const [bumpToken, setBumpToken] = useState(0);
    const url = exists ? `${tile.url}?t=${bumpToken || ""}` : null;

    return (
        <div
            className={`relative rounded-lg overflow-hidden border ${exists ? "border-[var(--vm-border)]" : "border-dashed border-[var(--vm-border)]/60"} bg-[var(--vm-input)]/30 aspect-square flex items-center justify-center group`}
            data-testid={`tile-${row.crop_key}-${bucket}`}
        >
            {exists ? (
                <img src={url} alt={`${row.common_name} ${bucket}`} className="w-full h-full object-cover" loading="lazy" />
            ) : (
                <div className="text-[10px] uppercase tracking-wider text-[var(--vm-text-muted)]">missing</div>
            )}
            <button
                type="button"
                onClick={async () => {
                    await onRegen(row, bucket);
                    setBumpToken(Date.now());
                }}
                disabled={busy}
                title={`Regenerate ${row.common_name} · ${bucket}`}
                data-testid={`regen-${row.crop_key}-${bucket}`}
                className="absolute bottom-1 right-1 w-6 h-6 rounded-full bg-[var(--vm-bg)]/95 border border-[var(--vm-border)] flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity disabled:opacity-40"
            >
                {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
            </button>
            <span className="absolute top-1 left-1 px-1.5 py-0.5 rounded bg-[var(--vm-bg)]/80 text-[9px] uppercase tracking-wider text-[var(--vm-text-secondary)]">
                {bucket}
            </span>
        </div>
    );
}

export default function AdminIllustrations() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [query, setQuery] = useState("");
    const [filter, setFilter] = useState("all");
    const [busyKey, setBusyKey] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            setRows(await adminListIllustrations());
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        return rows.filter((r) => {
            if (q && !r.common_name.toLowerCase().includes(q) && !r.crop_key.includes(q)) return false;
            if (filter === "incomplete" && r.completion >= 1) return false;
            if (filter === "missing" && r.completion > 0) return false;
            if (filter === "complete" && r.completion < 1) return false;
            return true;
        });
    }, [rows, query, filter]);

    const summary = useMemo(() => {
        const total = rows.length * 3;
        const present = rows.reduce((acc, r) => acc + Object.values(r.buckets).filter((b) => b.exists).length, 0);
        return { total, present };
    }, [rows]);

    const regen = async (row, bucket) => {
        const key = `${row.crop_key}-${bucket}`;
        setBusyKey(key);
        try {
            await adminRegenerateIllustration({ crop_key: row.crop_key, bucket });
            toast.success(`Regenerated ${row.common_name} · ${bucket}`);
            // Refresh row state to bump the URL cache-bust
            await load();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusyKey(null);
        }
    };

    return (
        <div className="space-y-8" data-testid="admin-illustrations-page">
            <PageHeader
                overline="Admin"
                title="Painterly illustrations"
                description="Browse and spot-fix the watercolour catalog. Tap regenerate on any tile that drifted."
            />

            <div className="flex flex-wrap items-center gap-3">
                <Link to="/admin">
                    <Button variant="ghost" data-testid="back-to-admin" className="text-[var(--vm-text-muted)]">
                        <ArrowLeft className="w-4 h-4 mr-2" /> Users
                    </Button>
                </Link>

                <div className="relative">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[var(--vm-text-muted)]" />
                    <Input
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder="Search crop…"
                        data-testid="illustrations-search"
                        className="pl-9 w-56 bg-[var(--vm-bg)] border-[var(--vm-border)]"
                    />
                </div>

                <div className="flex items-center gap-1">
                    <Filter className="w-4 h-4 text-[var(--vm-sage)] mr-1" />
                    {[
                        ["all", "All"], ["incomplete", "Incomplete"],
                        ["missing", "Missing"], ["complete", "Complete"],
                    ].map(([key, label]) => (
                        <button
                            key={key}
                            onClick={() => setFilter(key)}
                            data-testid={`filter-${key}`}
                            className={`px-3 py-1.5 rounded-full text-xs uppercase tracking-wider border transition-colors ${
                                filter === key
                                    ? "bg-[var(--vm-accent)] text-[var(--vm-bg)] border-[var(--vm-accent)]"
                                    : "border-[var(--vm-border)] text-[var(--vm-text-secondary)] hover:text-[var(--vm-text)]"
                            }`}
                        >
                            {label}
                        </button>
                    ))}
                </div>

                <div className="ml-auto flex items-center gap-3 text-xs text-[var(--vm-text-muted)]">
                    <Sparkles className="w-3.5 h-3.5 text-[var(--vm-accent)]" />
                    <span data-testid="catalog-progress">
                        <strong className="text-[var(--vm-text)]">{summary.present}</strong>
                        {" / "}
                        {summary.total} illustrations on disk
                    </span>
                    <Button
                        variant="ghost"
                        onClick={load}
                        disabled={loading}
                        data-testid="refresh-catalog"
                        className="text-[var(--vm-text-muted)]"
                    >
                        <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
                    </Button>
                </div>
            </div>

            {loading ? (
                <div className="text-sm text-[var(--vm-text-muted)] py-12 text-center">Loading catalog…</div>
            ) : filtered.length === 0 ? (
                <div className="journal-card p-10 text-center text-[var(--vm-text-muted)]" data-testid="empty-state">
                    No crops match the current filter.
                </div>
            ) : (
                <div className="space-y-3">
                    {filtered.map((row) => (
                        <article
                            key={row.crop_key}
                            data-testid={`row-${row.crop_key}`}
                            className="journal-card p-4 grid md:grid-cols-[200px_1fr] items-center gap-4"
                        >
                            <header>
                                <div className="font-serif-display text-xl text-[var(--vm-text)] capitalize">{row.common_name}</div>
                                <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--vm-text-muted)] mt-1 flex items-center gap-2">
                                    <span>{row.rotation_family}</span>
                                    <StatusPill ratio={row.completion} />
                                </div>
                            </header>
                            <div className="grid grid-cols-3 gap-2">
                                {BUCKETS.map((b) => (
                                    <BucketCell
                                        key={b}
                                        row={row}
                                        bucket={b}
                                        busy={busyKey === `${row.crop_key}-${b}`}
                                        onRegen={regen}
                                    />
                                ))}
                            </div>
                        </article>
                    ))}
                </div>
            )}
        </div>
    );
}
